/**
 * The line joining one node to the next.
 *
 * ## Why this is its own component
 *
 * The brief's hardest guarantee is that playing something nobody suggested is *not an
 * error*. Every product instinct pushes the other way — dash the line, grey it out, mark
 * it "off-book" — and every one of those turns the app into a thing that judges you.
 *
 * So there is exactly one connector component, it takes no "was this suggested" prop, and
 * its stroke width and opacity come from shared constants. A taken branch and a spontaneous
 * detour are drawn by the same code with the same numbers. `graph.test.tsx` asserts the
 * rendered attributes are identical across both cases, which makes the guarantee a
 * regression test rather than a good intention.
 */

import { CONNECTOR_OPACITY, CONNECTOR_STROKE_WIDTH, adventurousnessTint } from './motion.js';

export interface PathConnectorProps {
  /** Tints the curve only. Never changes its weight. */
  adventurousness?: number;
  /** Fades the whole connector, for the "previous set, still faded in place" case. */
  dimmed?: boolean;
  testId?: string;
}

export function PathConnector({
  adventurousness = 0.5,
  dimmed = false,
  testId = 'hc-connector',
}: PathConnectorProps): React.JSX.Element {
  return (
    <svg
      className="hc-connector"
      data-testid={testId}
      viewBox="0 0 48 120"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M0 60 L 48 60"
        fill="none"
        stroke={adventurousnessTint(adventurousness)}
        strokeWidth={CONNECTOR_STROKE_WIDTH}
        strokeLinecap="round"
        opacity={dimmed ? CONNECTOR_OPACITY * 0.35 : CONNECTOR_OPACITY}
        data-stroke-width={CONNECTOR_STROKE_WIDTH}
        data-opacity={CONNECTOR_OPACITY}
      />
    </svg>
  );
}

/**
 * The fan of curves from the current node out to every branch card.
 *
 * ## Why this is one SVG and not one per card
 *
 * The obvious implementation — a little connector inside each row — draws curves that
 * begin at the left edge of their own row rather than at the node, so they read as six
 * unrelated squiggles floating in a gutter. The lines have to *converge*, because
 * converging is the entire visual claim: these all come from where you are now.
 *
 * One SVG spanning the column's full height, with `preserveAspectRatio="none"` and a
 * 0–100 viewBox, does that in pure CSS terms. It relies on the rows being equal height —
 * which they are, by construction, so each path's endpoint lands on its card's centre.
 */
export function BranchFan({
  adventurousness,
  dimmed = false,
}: {
  /** One entry per card, top to bottom. */
  adventurousness: readonly number[];
  dimmed?: boolean;
}): React.JSX.Element | null {
  const count = adventurousness.length;
  if (count === 0) return null;
  return (
    <svg
      className="hc-fan"
      data-testid="hc-connector"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      {adventurousness.map((value, index) => {
        const y = ((index + 0.5) / count) * 100;
        return (
          <path
            key={index}
            d={`M0 50 C 46 50, 54 ${y}, 100 ${y}`}
            fill="none"
            stroke={adventurousnessTint(value)}
            strokeWidth={CONNECTOR_STROKE_WIDTH}
            strokeLinecap="round"
            // The fan is scaled non-uniformly by preserveAspectRatio, which would smear
            // the stroke width with it. This keeps every line the weight it declares.
            vectorEffect="non-scaling-stroke"
            opacity={dimmed ? CONNECTOR_OPACITY * 0.35 : CONNECTOR_OPACITY}
            data-stroke-width={CONNECTOR_STROKE_WIDTH}
            data-opacity={CONNECTOR_OPACITY}
          />
        );
      })}
    </svg>
  );
}
